import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useDataService } from '../../../../services/ApiDataService';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import useActioningContext from '../../../../utilities/ActioningProviderUtilities';
import useAlerts from '../../../../stores/AlertsStore';
import { getErrMessage } from '../../../../utilities/PromiseUtilities';
import FormSection from '../../../form/FormSection';
import { InfrastructureTableContext } from './InfrastructureTableContext';
import { InfrastructureDataProvider } from '../InfrastructureDataContext';

/**
 * Infrastructure table component with compound component pattern.
 *
 * This component replaces the boolean prop explosion pattern (7+ boolean props)
 * with a flexible composition pattern using compound components.
 *
 *
 * **Benefits:**
 * - Zero boolean props on parent component (down from 7)
 * - Flexible composition - include only what you need
 * - Clearer intent - presence of component shows feature is enabled
 * - Better maintainability - each compound component focused on single concern
 *
 * @param {Object} props
 * @param {React.ReactNode} props.title - FormSection title (e.g., <Trans>Interfaces</Trans>)
 * @param {string} props.tableId - Unique table identifier for state management
 * @param {string} props.emptyStateMessage - Message shown when no data available
 * @param {string} props.fetchUrl - API endpoint to fetch data from
 * @param {Function|Object} props.fetchParams - Query params (function(deviceId) or object)
 * @param {Array} props.columns - Column definitions for the table
 * @param {React.ReactNode} props.children - Compound child components (Content, etc.)
 * @param {React.ReactNode} [props.actionMenus] - Action menu component (passed to context)
 * @param {Function} [props.onSelectionChange] - Selection change handler (passed to context)
 * @param {number} [props.visibleRows=10] - Number of visible rows in table
 * @param {Function} [props.useStyles] - Custom styles hook (defaults to useSelectorStyles)
 * @param {Array} [props.dataArraySearchFields=[]] - Fields to search in data array
 * @param {Object} [props.apiRef] - Ref object for external API access
 */
const InfrastructureTable = ({
    title,
    tableId,
    emptyStateMessage,
    fetchUrl,
    fetchParams,
    columns,
    children,
    actionMenus,
    onSelectionChange,
    visibleRows = 10,
    useStyles,
    dataArraySearchFields = [],
    apiRef,
}) => {
    const { id: deviceId } = useRecordContext();
    const dataService = useDataService();
    const { isActioning, setIsActioning } = useActioningContext();
    const [, alertActions] = useAlerts();

    // Build fetch function with params
    const fetchFn = useCallback(() => {
        const params = typeof fetchParams === 'function' ? fetchParams(deviceId) : fetchParams;
        return dataService.get(fetchUrl, params);
    }, [deviceId, dataService, fetchUrl, fetchParams]);

    // Manual data fetching state (for tables with actions that need refetch)
    const [manualData, setManualData] = useState([]);
    const [manualEmptyText, setManualEmptyText] = useState('');
    const [lastLoadDataArrayTime, setLastLoadDataArrayTime] = useState(0);

    const manualFetch = useCallback(async () => {
        try {
            setIsActioning(true);
            const response = await fetchFn();
            const data = response?.data ?? response ?? [];
            setManualData(Array.isArray(data) ? data : []);
            setManualEmptyText(isEmpty(data) ? emptyStateMessage : '');
            setLastLoadDataArrayTime(Date.now());
        } catch (error) {
            setManualData([]);
            setManualEmptyText(emptyStateMessage);
            setLastLoadDataArrayTime(Date.now());
            alertActions.createErrorAlert(getErrMessage(error));
        } finally {
            setIsActioning(false);
        }
    }, [setIsActioning, fetchFn, emptyStateMessage, alertActions]);

    // Initial load for manual fetch mode, just for first time load
    useEffect(() => {
        if (deviceId) {
            manualFetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    // Expose manualFetch via apiRef for external triggering (e.g., after actions)
    useEffect(() => {
        if (apiRef) {
            apiRef.current = {
                actionPostFetch: manualFetch,
            };
        }
    }, [apiRef, manualFetch]);

    // Memoize columns (pass through from parent - parent is responsible for i18n memoization)
    const memoizedColumns = useMemo(() => columns, [columns]);

    // Create context value with all shared state
    const contextValue = useMemo(
        () => ({
            tableId,
            columns: memoizedColumns,
            data: manualData,
            emptyText: manualEmptyText,
            actionMenus,
            onSelectionChange,
            visibleRows,
            useStyles,
            dataArraySearchFields,
            apiRef,
            isActioning,
            setIsActioning,
            lastLoadDataArrayTime,
        }),
        [
            tableId,
            memoizedColumns,
            manualData,
            manualEmptyText,
            actionMenus,
            onSelectionChange,
            visibleRows,
            useStyles,
            dataArraySearchFields,
            apiRef,
            isActioning,
            setIsActioning,
            lastLoadDataArrayTime,
        ]
    );

    return (
        <FormSection title={title} addSpacer>
            {/* Split context for granular re-render control */}
            <InfrastructureDataProvider
                data={manualData}
                columns={memoizedColumns}
                emptyText={manualEmptyText}
                isActioning={isActioning}
            >
                <InfrastructureTableContext.Provider value={contextValue}>
                    {children}
                </InfrastructureTableContext.Provider>
            </InfrastructureDataProvider>
        </FormSection>
    );
};

InfrastructureTable.propTypes = {
    title: PropTypes.node.isRequired,
    tableId: PropTypes.string.isRequired,
    emptyStateMessage: PropTypes.string.isRequired,
    fetchUrl: PropTypes.string.isRequired,
    fetchParams: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    columns: PropTypes.array.isRequired,
    children: PropTypes.node.isRequired,
    actionMenus: PropTypes.node,
    onSelectionChange: PropTypes.func,
    visibleRows: PropTypes.number,
    useStyles: PropTypes.func,
    dataArraySearchFields: PropTypes.array,
    apiRef: PropTypes.shape({
        current: PropTypes.object,
    }),
};

InfrastructureTable.defaultProps = {
    actionMenus: null,
    onSelectionChange: null,
    visibleRows: 10,
    useStyles: null,
    dataArraySearchFields: [],
    apiRef: null,
};

export default InfrastructureTable;
